'use client';

// Requirements:
// - Staff/admin users should not be able to enroll/register on public pages
//   while they're acting in their staff capacity.
// - Coach + Parent persona toggle: if the user is a coach who ALSO has the
//   parent persona available (every coach now does, post Phase 1 trigger),
//   show a "Switch to Parent view" CTA instead of telling them to use a
//   separate personal email — they can simply flip personas and continue
//   the enrollment as a parent.
// - Pure staff (no parent capability) get the original message.
// - U8 copy sweep (plan 2026-05-23-002): user-facing strings updated to
//   account-holder-neutral language (athlete vs child) on the staff block.

import { useRouter } from 'next/navigation';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import Button from '@/components/ui/button/Button';
import { useUserRole } from '@/context/UserRoleContext';
import { useSelfServiceAccess } from '@/hooks/auth/useSelfServiceAccess';

interface StaffRegistrationGuardProps {
  gymSlug?: string;
}

/**
 * Renders a full-page block message for staff users who land on public registration pages.
 * Callers are responsible for checking userType === 'staff' before rendering this component.
 */
export default function StaffRegistrationGuard({ gymSlug }: StaffRegistrationGuardProps) {
  const router = useRouter();
  const { currentUser, setActivePersona } = useUserRole();
  const canSwitchToParent = !!currentUser?.availablePersonas?.includes('parent');

  if (canSwitchToParent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <GoogleMaterialIcon name="family_restroom" size="lg" className="text-pink-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Use your parent account to enroll</h2>
          <p className="text-gray-600 text-sm mb-6">
            You&apos;re signed in as a coach. To enroll your own athlete in a program, switch to your parent account — your coaching account stays unchanged.
          </p>
          <div className="flex flex-col gap-3">
            <Button variant="primary" onClick={() => setActivePersona('parent')}>
              <GoogleMaterialIcon name="favorite" size="sm" className="mr-2" />
              Switch to My Parent Account
            </Button>
            {gymSlug && (
              <Button variant="outline" onClick={() => router.push(`/programs/${gymSlug}`)}>
                <GoogleMaterialIcon name="arrow_back" size="sm" className="mr-2" />
                Back to Program Catalog
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <GoogleMaterialIcon name="admin_panel_settings" size="lg" className="text-blue-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Staff Account Detected</h2>
        <p className="text-gray-600 text-sm mb-6">
          This page is for account holders and athletes to register for programs. To manage registrations, use the admin dashboard.
        </p>
        <div className="flex flex-col gap-3">
          <Button variant="primary" onClick={() => router.push('/dashboard')}>
            <GoogleMaterialIcon name="dashboard" size="sm" className="mr-2" />
            Go to Dashboard
          </Button>
          {gymSlug && (
            <Button variant="outline" onClick={() => router.push(`/programs/${gymSlug}`)}>
              <GoogleMaterialIcon name="arrow_back" size="sm" className="mr-2" />
              Back to Program Catalog
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Shown when the viewer is signed in but we could not work out who they are.
 *
 * Distinct from the staff block on purpose: telling a parent "you're signed in
 * as a coach" because a lookup failed is a false statement they cannot act on,
 * and the persona switch offered by the staff block cannot work here anyway —
 * switching needs a selected gym, and these pages have none. Reloading is the
 * thing that actually retries the lookup, so that is what we offer.
 *
 * The copy does NOT diagnose the cause or promise nothing was charged. This can
 * fire on a dropped connection, on a missing auth_users row, or on an RLS
 * denial — reloading only helps the first — and on the enroll page the guard is
 * a render-time early return that sits above the payment modal, so it could in
 * principle replace a screen where an invoice already exists.
 */
export function AccountCheckFailedGuard({ gymSlug }: StaffRegistrationGuardProps) {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-theme-lg border border-gray-200 p-8 text-center">
        <GoogleMaterialIcon name="sync_problem" size="lg" className="text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">We couldn&apos;t check your account</h2>
        <p className="text-gray-600 text-sm mb-6">
          Something went wrong on our side while checking your account. Reload the page to try again. If it keeps happening, contact your gym and they can sign you up directly.
        </p>
        <div className="flex flex-col gap-3">
          <Button variant="primary" onClick={() => window.location.reload()}>
            <GoogleMaterialIcon name="refresh" size="sm" className="mr-2" />
            Reload and try again
          </Button>
          {gymSlug && (
            <Button variant="outline" onClick={() => router.push(`/programs/${gymSlug}`)}>
              <GoogleMaterialIcon name="arrow_back" size="sm" className="mr-2" />
              Back to Program Catalog
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Inline sibling of the above, for detail/catalog pages where the page itself should keep rendering. */
export function AccountCheckFailedMessage({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  return (
    <div className={`w-full px-4 ${size === 'md' ? 'py-4' : 'py-3'} bg-amber-50 ${size === 'md' ? 'border-amber-200' : 'border-amber-100'} border rounded-lg ${size === 'md' ? 'text-sm' : 'text-xs'} text-amber-800 text-center`}>
      <GoogleMaterialIcon name="sync_problem" size="xs" className="mb-1 block mx-auto" />
      <div className="mb-2">We couldn&apos;t check your account, so signing up is unavailable right now. Reload to try again.</div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-1.5 text-xs font-medium text-white transition-colors"
      >
        <GoogleMaterialIcon name="refresh" size="xs" />
        Reload and try again
      </button>
    </div>
  );
}

/**
 * Inline staff message for catalog page cards and detail pages (where full-page block isn't appropriate).
 * Use size="sm" (default) on catalog cards, size="md" on detail page CTA areas.
 *
 * Coach + Parent persona toggle: when the user has parent persona available,
 * show a one-tap "Switch to Parent view" button instead of the
 * "register-a-separate-email" copy. Otherwise (pure staff, no parents row),
 * fall back to the original message.
 */
export function StaffRegistrationMessage({ size = 'sm', gymName }: { size?: 'sm' | 'md'; gymName?: string }) {
  const { currentUser, setActivePersona } = useUserRole();
  const access = useSelfServiceAccess();
  const canSwitchToParent = !!currentUser?.availablePersonas?.includes('parent');

  // Callers withhold the sign-up CTA whenever self-service is unavailable, which
  // covers both "this is staff" and "we could not check". Only this component
  // knows which sentence is true, so it picks — a viewer we failed to identify
  // must not be told they are staff.
  if (access === 'unconfirmed') {
    return <AccountCheckFailedMessage size={size} />;
  }

  if (canSwitchToParent) {
    return (
      <div className={`w-full px-4 ${size === 'md' ? 'py-4' : 'py-3'} bg-pink-50 ${size === 'md' ? 'border-pink-200' : 'border-pink-100'} border rounded-2xl ${size === 'md' ? 'text-sm' : 'text-xs'} text-pink-800 text-center`}>
        <GoogleMaterialIcon name="family_restroom" size="xs" className="mb-1 block mx-auto" />
        <div className="mb-2">
          You&apos;re signed in as a coach. To enroll your own child, switch to your parent account.
        </div>
        <button
          type="button"
          onClick={() => setActivePersona('parent')}
          className={`inline-flex items-center gap-1 rounded-md bg-pink-600 hover:bg-pink-700 px-3 py-1.5 text-xs font-medium text-white transition-colors`}
        >
          <GoogleMaterialIcon name="favorite" size="xs" />
          Switch to My Parent Account
        </button>
      </div>
    );
  }

  return (
    <div className={`w-full px-4 ${size === 'md' ? 'py-4' : 'py-3'} bg-blue-50 ${size === 'md' ? 'border-blue-200' : 'border-blue-100'} border rounded-2xl ${size === 'md' ? 'text-sm' : 'text-xs'} text-blue-800 text-center`}>
      <GoogleMaterialIcon name="admin_panel_settings" size="xs" className="mb-1 block mx-auto" />
      {gymName ? (
        <>You&apos;re signed in as a <span className="font-semibold">{gymName}</span> staff account. Staff accounts can&apos;t enroll in any gym&apos;s programs. To enroll yourself or your athlete, register a separate personal email as an account holder or independent athlete.</>
      ) : (
        <>You&apos;re signed in as a staff account. Staff accounts can&apos;t enroll in any gym&apos;s programs. To enroll yourself or your athlete, register a separate personal email as an account holder or independent athlete.</>
      )}
    </div>
  );
}
